import * as React from "react"
import type { DisplayTier } from "./types"

interface TierPillProps {
  tier: DisplayTier | null
  className?: string
}

/**
 * Tier badge palette — exactly the colors specified in the handoff §6:
 *
 *   Popular → orange-10  / orange-700 (warm tint)
 *   Luxury  → gray-900   / white      (premium dark)
 *   Value   → #f2f7ff    / blue       (trust tint; 4.56:1, where the old 10% blue
 *                                     tint gave 4.26:1, FRIDGE-1504 L1504-3)
 *   Budget  → gray-100   / gray-700   (neutral)
 *   Niche   → transparent / gray-600 with gray-200 1px border
 */
const PALETTE: Record<DisplayTier, { bg: string; fg: string; border?: string }> = {
  popular: { bg: "rgba(255,153,81,0.10)", fg: "#c2410c" },
  luxury: { bg: "#111827", fg: "#ffffff" },
  value: { bg: "#f2f7ff", fg: "#0469f0" },
  budget: { bg: "#f3f4f6", fg: "#4b5563" },
  niche: { bg: "transparent", fg: "#4b5563", border: "1px solid #e5e7eb" },
}

const LABELS: Record<DisplayTier, string> = {
  popular: "Popular",
  luxury: "Luxury",
  value: "Value",
  budget: "Budget",
  niche: "Niche",
}

export function TierPill({ tier, className = "" }: TierPillProps) {
  if (!tier) return null
  const p = PALETTE[tier]
  if (!p) return null // defensive — shouldn't happen now that dbTierToDisplay sanitises
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-[2px] text-[10.5px] font-bold uppercase tracking-[0.04em] ${className}`}
      style={{ background: p.bg, color: p.fg, border: p.border }}
    >
      {LABELS[tier]}
    </span>
  )
}
